import { ChangeDetectorRef, computed, inject, Injectable, signal } from "@angular/core";
import { Prescription } from "@project/models/prescriptions/prescription";
import { SrxPrescriptionService } from "@project/services/srx-prescriptions.service";
import { ShareFormDataDTO } from "@main/formio/dto/shareFormDataDTO";
import {
    BaseResolvableDialogService
} from "@project/prescription/submit-to-srx-with-pin-dialog/base-resolvable-dialog.service";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { SelectItem } from "primeng/api";
import { UserSuggestion } from "@project/models";
import { FormButtonsConfig } from "@mipro/components";
import { SharedFileService } from "@mipro/services/shared-file.service";
import { FormioDataViewerService } from "@main/formio/services/formioDataViewer.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { UserService } from "@main/users";
import { RoleService } from "@main/roles";
import { MultiSelectChangeEvent } from "primeng/multiselect";

export enum ShareFormDialogResultStatus {
    Canceled,
    Success
}

export interface ShareFormDialogResult {
    status: ShareFormDialogResultStatus;
    data: ShareFormDataDTO;
}

export interface ShareFormDialogData {
    formInstanceId: string;
    shareFileId: number;
}

@Injectable({
    providedIn: "root"
})
export class FormIoShareDialogService extends BaseResolvableDialogService<ShareFormDialogResult, ShareFormDialogData> {
    private sharedFileService = inject(SharedFileService);
    private userHelperService = inject(UserHelperService);
    private roleService = inject(RoleService);
    private fb = inject(UntypedFormBuilder);

    shareFileForm: UntypedFormGroup;

    userTypeOptions: SelectItem[] = [];
    userSuggestions: UserSuggestion[] = [];
    allUsers: UserSuggestion[];

    sharingFile = false;

    shareFormDialogData: ShareFormDialogData = null;

    get formEmails(): string[] {
        return this.shareFileForm.get("emails").value
    }

    get formUsers(): UserSuggestion[] {
        return this.shareFileForm.get("users").value
    }

    protected async onShow(data: ShareFormDialogData): Promise<void> {
        this.initForm();
        await this.initUserTypes();
        await this.initUsers();
        this.shareFormDialogData = data;
        return;
    }

    private initForm(): void {
        this.shareFileForm = this.fb.group({
            fileDetailsId: [null],
            userTypes: [[]],
            users: [[]],
            emails: [[], [Validators.required, this.validateEmails]]
        });
    }

    private async initUserTypes() {
        const roles = await this.roleService.getCurrentOrganizationRoles();
        this.userTypeOptions = roles.map(x => ({label: x.name, value: x.id}));
    }

    private async initUsers() {
        const users = await this.sharedFileService.getUsersPage([
            {searchTerm: this.userHelperService.hashedOrganizationId, field: "organizationId"}
        ]);

        this.userSuggestions = this.allUsers = users.items;
    }

    async filterUsers(event: any) {
        const searchTerm = event.filter;
        const selectedUserTypes = this.shareFileForm.get("userTypes").value;

        if (selectedUserTypes && selectedUserTypes.length > 0) {
            this.allUsers = [];
            for (const userType of selectedUserTypes) {
                const users = await this.sharedFileService.getUsersPage([
                    {searchTerm: searchTerm, field: "fullName"},
                    {searchTerm: userType, field: "roleId"},
                    {searchTerm: this.userHelperService.hashedOrganizationId, field: "organizationId"}
                ]);
                this.allUsers = this.allUsers.concat(users.items);
            }
            this.userSuggestions = this.allUsers.filter((user, index, self) =>
                index === self.findIndex((t) => t.id === user.id)
            );
        } else {
            await this.filterUsersForAllRoles(searchTerm);
        }
    }

    async filterUsersForAllRoles(name: string = null) {
        if (!this.userTypeOptions.length) {
            await this.initUserTypes();
        }

        let allUsers = [];

        for (const option of this.userTypeOptions) {
            const users = await this.sharedFileService.getUsersPage([
                {searchTerm: name, field: "fullName"},
                {searchTerm: option.value, field: "roleId"},
                {searchTerm: this.userHelperService.hashedOrganizationId, field: "organizationId"}
            ]);

            allUsers = allUsers.concat(users.items);

        }
        this.userSuggestions = allUsers.filter((user, index, self) =>
            index === self.findIndex((t) => t.id === user.id)
        );
    }

    async onUserChange(event: MultiSelectChangeEvent) {
        const selectMultipleUsers = !event.itemValue;
        if (selectMultipleUsers) {
            const users = event.value as UserSuggestion[];
            const usersDeselected = !users.length;
            if (usersDeselected) {
                const emails = this.formEmails
                    .filter(email => !this.allUsers.find(user => user.email === email));

                this.shareFileForm.patchValue({
                    emails: [...emails]
                });
            } else {
                const emails = users
                    .filter(user => !this.formEmails.includes(user.email) && user.email)
                    .map(user => user.email);
                this.shareFileForm.patchValue({
                    emails: [...emails, ...this.formEmails]
                });
            }
            return;
        }

        let user = this.allUsers.find(x => x.id == event.itemValue.id)
        if (!user || !user.email) {
            return;
        }

        const isRemove = !event.value.some(x => x.id === event.itemValue.id);
        if (isRemove) {
            this.shareFileForm.patchValue({
                emails: [...this.formEmails?.filter(email => email != user.email),]
            });
        } else {
            this.shareFileForm.patchValue({
                emails: [user.email, ...this.formEmails]
            });
        }
    }

    addEmail(email: string) {
        let user = this.allUsers.find(x => x.email == email);
        if (!!user) {
            this.shareFileForm.patchValue({users: [user, ...this.formUsers]});
        }
    }

    removeEmail(email: string) {
        let user = this.allUsers.find(x => x.email == email);
        if (!!user) {
            this.shareFileForm.patchValue({users: this.formUsers.filter(x => x.id != user.id)});
        }
    }

    validateEmails(control: AbstractControl): ValidationErrors | null {
        const emails = control.value;
        if (!emails || emails.length === 0) {
            return {required: true};
        }
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const invalidEmails = emails.filter(email => !emailPattern.test(email));
        return invalidEmails.length > 0 ? {invalidEmails: invalidEmails} : null;
    }

    async share() {
        if (this.shareFileForm.invalid) {
            return;
        }

        this.sharingFile = true;

        let formValue = this.shareFileForm.value;

        let data: ShareFormDataDTO = {
            userIds: formValue.users.map(x => x.id),
            emails: formValue.emails,
            fileDetailsId: this.shareFormDialogData.shareFileId as any,
        }

        try {
            this.closeWithResult({
                status: ShareFormDialogResultStatus.Success,
                data: data
            });
        } finally {
            this.sharingFile = false;
        }
    }

    close() {
        this.closeWithResult({
            data: null,
            status: ShareFormDialogResultStatus.Canceled
        });
    }
}

